import { canEditAnySubmission } from '@cult/domain';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { InlineError, PageContainer } from '../../components';
import { FeedbackForm, useDirectFeedback } from '../../features/direct-feedback';
import { FORM_ORIGINS, type FormOrigin } from '../../lib/form-origins';
import { useCurrentUser } from '../../state/use-current-user';

type Search = {
  origin?: FormOrigin;
};

const EditFeedbackPage = () => {
  const { id } = Route.useParams();
  const { origin = FORM_ORIGINS.FEED } = Route.useSearch();
  const feedbackQuery = useDirectFeedback(id);
  const user = useCurrentUser((s) => s.user);

  if (feedbackQuery.isPending) {
    return (
      <PageContainer>
        <Skeleton active paragraph={{ rows: 6 }} />
      </PageContainer>
    );
  }

  if (feedbackQuery.isError) {
    return (
      <PageContainer>
        <InlineError title="Couldn't load feedback" detail={feedbackQuery.error.message} />
      </PageContainer>
    );
  }

  // Same rule as the API: the author or an admin (moderators get no override). The form handles the closed edit window.
  const canEdit = user !== null && (feedbackQuery.data.author.id === user.id || canEditAnySubmission(user.role));
  if (!canEdit) {
    return (
      <PageContainer>
        <InlineError title="You can't edit this feedback" detail="Only its author can change it." />
      </PageContainer>
    );
  }

  return <FeedbackForm origin={origin} feedback={feedbackQuery.data} />;
};

export const Route = createFileRoute('/_auth/feedback/$id_/edit')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    origin: search.origin === FORM_ORIGINS.ADMIN ? FORM_ORIGINS.ADMIN : undefined,
  }),
  component: EditFeedbackPage,
});
